import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/ui/Header';
import Sidebar from '../components/ui/Sidebar';
import Breadcrumb from '../components/ui/Breadcrumb';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import { usePreferences } from '../context/PreferencesContext';

const Preferences = () => {
  const navigate = useNavigate();
  const { preferences, updatePreference, updatePreferences } = usePreferences();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Options
  const themeOptions = [
    { value: 'light', label: 'Light', icon: 'Sun', desc: 'Crisp light interface' },
    { value: 'dark', label: 'Dark', icon: 'Moon', desc: 'Easy on the eyes in low light' },
    { value: 'system', label: 'System', icon: 'Monitor', desc: 'Syncs with device appearance' }
  ];

  const fontSizeOptions = [
    { value: 'small', label: 'Small', size: '12px' },
    { value: 'medium', label: 'Medium', size: '14px' },
    { value: 'large', label: 'Large', size: '16px' }
  ];

  // Handle preference change with real-time updates via context
  const handlePreferenceChange = (field, value) => {
    updatePreference(field, value);
  };

  // Save all preferences
  const handleSavePreferences = async () => {
    setIsSaving(true);
    setErrorMessage('');
    
    try {
      await new Promise(resolve => setTimeout(resolve, 400));
      updatePreferences(preferences);
      setSuccessMessage('Appearance & preferences updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving preferences:', error);
      setErrorMessage('Failed to save preferences. Please try again.');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  // Reset to defaults
  const handleResetToDefaults = () => {
    const defaultPreferences = {
      theme: 'system',
      compactMode: false,
      animations: true,
      highContrast: false,
      fontSize: 'medium',
      reducedMotion: false
    };
    updatePreferences(defaultPreferences);
    setSuccessMessage('Reset to default preferences successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Reset to defaults
  const handleResetDefaults = () => {
    if (window.confirm('Are you sure you want to reset all preferences to default values?')) {
      handleResetToDefaults();
    }
  };

  // Export preferences
  const handleExportPreferences = () => {
    const dataStr = JSON.stringify(preferences, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `preferences_${new Date().toISOString().slice(0,19)}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    setSuccessMessage('Preferences exported successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Import preferences
  const handleImportPreferences = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedPreferences = JSON.parse(e.target.result);
          setPreferences(importedPreferences);
          
          // Apply imported settings
          applyTheme(importedPreferences.theme);
          applyFontSize(importedPreferences.fontSize);
          applyCompactMode(importedPreferences.compactMode);
          applyReducedMotion(importedPreferences.reducedMotion);
          applyHighContrast(importedPreferences.highContrast);
          applyAnimations(importedPreferences.animations);
          
          // Save to localStorage
          Object.keys(importedPreferences).forEach(key => {
            localStorage.setItem(key, importedPreferences[key]);
          });
          localStorage.setItem('userPreferences', JSON.stringify(importedPreferences));
          
          setSuccessMessage('Preferences imported successfully!');
          setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
          setErrorMessage('Invalid preferences file');
          setTimeout(() => setErrorMessage(''), 3000);
        }
      };
      reader.readAsText(file);
    }
    event.target.value = ''; // Reset file input
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <main className={`pt-16 transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-72'}`}>
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      
      <main className={`pt-16 transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-72'}`}>
        <div className="p-6">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground">Preferences</h1>
            <p className="text-muted-foreground mt-2">
              Customize your app appearance and accessibility settings
            </p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 p-4 bg-green-100 dark:bg-green-900/20 border border-green-400 dark:border-green-700 rounded-lg animate-in fade-in slide-in-from-top-2">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={18} className="text-green-600 dark:text-green-400" />
                <span className="text-sm text-green-800 dark:text-green-300">{successMessage}</span>
              </div>
            </div>
          )}

          {/* Error Message */}
          {errorMessage && (
            <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-700 rounded-lg animate-in fade-in slide-in-from-top-2">
              <div className="flex items-center space-x-2">
                <Icon name="XCircle" size={18} className="text-red-600 dark:text-red-400" />
                <span className="text-sm text-red-800 dark:text-red-300">{errorMessage}</span>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* Appearance Section */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Icon name="Palette" size={18} className="mr-2 text-blue-600 dark:text-blue-400" />
                Appearance
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Interface Theme Mode
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {themeOptions.map((opt) => {
                      const isActive = (preferences.theme || 'system') === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => handlePreferenceChange('theme', opt.value)}
                          className={`flex flex-col items-start p-3.5 rounded-xl border text-left transition-all ${
                            isActive
                              ? 'border-blue-500 bg-blue-50/70 dark:bg-blue-950/40 ring-2 ring-blue-500/20 text-blue-900 dark:text-blue-200'
                              : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 bg-background text-foreground'
                          }`}
                        >
                          <div className="flex items-center justify-between w-full mb-1.5">
                            <div className="flex items-center gap-2">
                              <span className={`p-1.5 rounded-lg ${isActive ? 'bg-blue-600 text-white' : 'bg-muted text-muted-foreground'}`}>
                                <Icon name={opt.icon} size={16} />
                              </span>
                              <span className="font-semibold text-sm">{opt.label}</span>
                            </div>
                            {isActive && (
                              <span className="text-blue-600 dark:text-blue-400">
                                <Icon name="CheckCircle2" size={16} />
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground mt-0.5">{opt.desc}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Compact Mode</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Reduce padding and spacing for denser layout</p>
                  </div>
                  <button
                    onClick={() => handlePreferenceChange('compactMode', !preferences.compactMode)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      preferences.compactMode ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences.compactMode ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Animations</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Enable smooth transitions and animations</p>
                  </div>
                  <button
                    onClick={() => handlePreferenceChange('animations', !preferences.animations)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      preferences.animations ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences.animations ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>
            </div>

            {/* Accessibility */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Icon name="Accessibility" size={18} className="mr-2 text-blue-600 dark:text-blue-400" />
                Accessibility
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">High Contrast</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Increase contrast for better visibility</p>
                  </div>
                  <button
                    onClick={() => handlePreferenceChange('highContrast', !preferences.highContrast)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      preferences.highContrast ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences.highContrast ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Font Size</label>
                  <div className="flex space-x-2">
                    {fontSizeOptions.map((size) => (
                      <button
                        key={size.value}
                        onClick={() => handlePreferenceChange('fontSize', size.value)}
                        className={`flex-1 flex flex-col items-center space-y-1 px-4 py-2 rounded-lg border transition-all ${
                          preferences.fontSize === size.value
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <span style={{ fontSize: size.size }}>Aa</span>
                        <span className="text-xs">{size.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Reduced Motion</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Minimize animations and transitions</p>
                  </div>
                  <button
                    onClick={() => handlePreferenceChange('reducedMotion', !preferences.reducedMotion)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      preferences.reducedMotion ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences.reducedMotion ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between gap-4 pt-4">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleResetDefaults}
                  iconName="Refresh"
                  iconPosition="left"
                >
                  Reset to Defaults
                </Button>
                <Button
                  variant="outline"
                  onClick={handleExportPreferences}
                  iconName="Download"
                  iconPosition="left"
                >
                  Export
                </Button>
                <label className="cursor-pointer">
                  <Button
                    variant="outline"
                    iconName="Upload"
                    iconPosition="left"
                    onClick={() => document.getElementById('importFile').click()}
                  >
                    Import
                  </Button>
                  <input
                    id="importFile"
                    type="file"
                    accept=".json"
                    className="hidden"
                    onChange={handleImportPreferences}
                  />
                </label>
              </div>
              <Button
                variant="default"
                onClick={handleSavePreferences}
                isLoading={isSaving}
                iconName="Save"
                iconPosition="left"
              >
                Save All Preferences
              </Button>
            </div>
          </div>
        </div>
      </main>

      <footer className={`bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 py-4 transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-72'}`}>
        <div className="px-6">
          <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
            <div className="flex items-center space-x-4">
              <span>© {new Date()?.getFullYear()} AssetTracker Pro</span>
              <span>•</span>
              <span>Preferences</span>
            </div>
            <div className="flex items-center space-x-4">
              <span>Last updated: {new Date().toLocaleDateString()}</span>
              <span>•</span>
              <span>Version 1.0.0</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Preferences;